import { Link } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const BUYER_CHECKLIST = [
  ['Lead workflow', 'Can you see every open opportunity and the next action without searching through texts, email and notes?'],
  ['Fence-specific estimating', 'Can pricing handle footage, gates and tear-out instead of forcing every quote into a generic line-item workflow?'],
  ['Quote follow-up', 'Does a sent estimate stay visible until it is accepted, lost or intentionally postponed?'],
  ['Scheduling handoff', 'Can accepted work move into an install date, crew assignment and production handoff without re-entering the job?'],
  ['Field context', 'Can the crew see scope, schedule, materials and notes in one place?'],
  ['Billing visibility', 'Can the office see invoices, balances and recorded payments alongside the job?'],
];

const FAQ = [
  ['What is fence contractor software?', 'Fence contractor software is a business workflow system for managing customers, estimates, proposals, follow-up, scheduling, production and billing around fence installation work.'],
  ['What should a small fence company look for first?', 'Start with the stages that cause the most rework or missed revenue. For many small contractors that means lead response, repeatable estimating, quote follow-up and the handoff from accepted estimate to scheduled job.'],
  ['Does PicketOps replace accounting software?', 'No. PicketOps tracks job-related invoice, balance and payment records inside the contractor workflow, but it is not positioned as a full accounting ledger or tax system.'],
  ['Does PicketOps send customer text messages automatically?', 'Not currently. Email and SMS entries are prepared as drafts for contractor review; outbound messaging is not connected.'],
  ['Can PicketOps work for a one-person fence company?', 'Yes. The workflow is designed to be useful before a contractor has a large office staff: organize leads, price quotes, remember follow-up, schedule accepted work and keep job context together.'],
];

export default function FenceContractorSoftwareGuide() {
  const { isAuthenticated } = useAuth();
  const trialTarget = isAuthenticated ? '/dashboard' : '/contractor/signup';

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <a href="#software-guide" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-3 focus:text-primary-foreground">
        Skip to fence contractor software guide
      </a>

      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span>
            <span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span>
            <span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span>
          </span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Fence contractor software</span>
        </nav>
      </header>

      <main id="software-guide" className="max-w-6xl mx-auto px-4 pb-16 space-y-12">
        <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-md)">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Fence contractor software buyer guide</p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">
                Fence contractor software for estimates, follow-up, scheduling and jobs
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
                The useful question is not how many features a platform has. It is whether the system keeps a fence job moving from the first inquiry to the final payment without losing scope, follow-up or ownership along the way.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild><Link to={trialTarget}>{isAuthenticated ? 'Open PicketOps' : 'Create account'} <ApperIcon name="ArrowRight" size={16}/></Link></Button>
                <Button variant="outline" asChild><Link to="/contractor-revenue-scorecard">Take the free revenue scorecard</Link></Button>
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">The core workflow</p>
              <ol className="mt-5 space-y-3">
                {[
                  ['Lead', 'Capture customer and project context'],
                  ['Estimate', 'Price footage, gates and tear-out'],
                  ['Follow-up', 'Keep sent quotes active until resolved'],
                  ['Job', 'Schedule sold work and assign the crew'],
                  ['Production', 'Carry scope and field notes into the install'],
                  ['Closeout', 'Walk the finished job, clear punch-list items and verify billing readiness'],
                  ['Money', 'Keep job-related billing records visible'],
                ].map(([title, detail], index) => (
                  <li key={title} className="flex gap-3 rounded-lg border border-border bg-background p-3">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">{index + 1}</span>
                    <div><p className="text-sm font-semibold">{title}</p><p className="mt-1 text-xs text-muted-foreground">{detail}</p></div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section aria-labelledby="buyer-checklist-heading">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">What to evaluate</p>
            <h2 id="buyer-checklist-heading" className="mt-2 text-3xl font-heading font-semibold">Six questions to ask before choosing fence business software.</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">A long feature list can still leave the daily workflow fragmented. Evaluate the handoffs between stages, not only the individual screens.</p>
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {BUYER_CHECKLIST.map(([title, detail], index) => (
              <Card key={title}>
                <CardContent className="p-5 flex gap-4">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-primary text-sm font-bold">{index + 1}</span>
                  <div><h3 className="font-heading text-lg font-semibold">{title}</h3><p className="mt-2 text-sm leading-relaxed text-muted-foreground">{detail}</p></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="grid lg:grid-cols-[0.9fr_1.1fr] overflow-hidden rounded-2xl border border-border bg-card">
          <div className="p-6 sm:p-8 bg-primary/5">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">How PicketOps approaches it</p>
            <h2 className="mt-2 text-3xl font-heading font-semibold">Optimize the revenue path, not the number of menus.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              PicketOps is organized around the decisions a fence contractor needs to make next: respond to the lead, price the work, follow up on the quote, schedule accepted work, prepare production and finish the billing loop.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild><Link to="/#workflow-tour">See the workflow <ApperIcon name="ArrowRight" size={16}/></Link></Button>
              <Button variant="outline" asChild><Link to="/fence-profit-margin-calculator">Try Profit Guard</Link></Button>
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <dl className="divide-y divide-border">
              {[
                ['Revenue Command Center', 'Surfaces quoted work waiting on follow-up, scheduling work and the next suggested action.'],
                ['Profit Guard', 'Checks selling price against entered direct costs and target gross margin without confusing markup and margin.'],
                ['Quote Rescue', 'Turns due sent estimates into a visible queue and prepares the next follow-up draft.'],
                ['Post-job revenue', 'Lets completed jobs prepare review and referral drafts while the customer experience is still fresh.'],
              ].map(([term, detail]) => (
                <div key={term} className="py-4">
                  <dt className="font-semibold">{term}</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{detail}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section aria-labelledby="software-tools-heading">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">Evaluate before you buy</p>
              <h2 id="software-tools-heading" className="mt-2 text-3xl font-heading font-semibold">Use the free PicketOps tools first.</h2>
            </div>
            <p className="max-w-md text-sm text-muted-foreground">Each tool works without an account, so you can test the operating ideas before committing to the software.</p>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ['Gauge', 'Revenue scorecard', 'Find the weakest stage in your lead-to-job process.', '/contractor-revenue-scorecard'],
              ['Ruler', 'Measurement worksheet', 'Break the field layout into runs and total the footage.', '/fence-measurement-worksheet'],
              ['Boxes', 'Material takeoff', 'Plan approximate sections, posts, rails and pickets.', '/fence-material-takeoff-calculator'],
              ['Calculator', 'Estimate calculator', 'Price footage, gates, tear-out and other charges using your rates.', '/fence-estimate-calculator'],
              ['FilePenLine', 'Scope builder', 'Turn project facts into a copy-ready fence scope.', '/fence-estimate-template'],
              ['Users', 'Labor & crew planner', 'Plan crew-days, worker-hours and direct labor cost from your assumptions.', '/fence-labor-crew-planner'],
              ['CalendarDays', 'Install schedule planner', 'Turn crew workdays into a working start and finish window.', '/fence-install-schedule-planner'],
              ['FileDiff', 'Change order planner', 'Document added or removed scope, price and schedule impact.', '/fence-change-order-planner'],
              ['ClipboardCheck', 'Final walkthrough', 'Clear punch-list items and verify closeout readiness.', '/fence-final-walkthrough-checklist'],
              ['Columns3', 'Proposal options', 'Compare real Good, Better and Best-style choices.', '/fence-proposal-option-builder'],
              ['ShieldCheck', 'Profit Guard', 'Compare price, direct costs, gross margin and markup.', '/fence-profit-margin-calculator'],
              ['MessagesSquare', 'Follow-up guide', 'Use a five-touch fence estimate follow-up sequence.', '/fence-estimate-follow-up'],
              ['ClipboardCheck', 'Estimate checklist', 'Review scope and assumptions before sending the quote.', '/fence-estimate-checklist'],
            ].map(([icon, title, detail, to]) => (
              <Link key={title} to={to} className="group rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-(--shadow-md) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name={icon} size={19}/></span>
                <h3 className="mt-4 font-heading text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{detail}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">Open tool <ApperIcon name="ArrowRight" size={15} className="transition-transform group-hover:translate-x-0.5"/></span>
              </Link>
            ))}
          </div>
        </section>

        <section aria-labelledby="software-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="software-faq-heading" className="text-2xl font-heading font-semibold">Fence contractor software questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">
            {FAQ.map(([question, answer]) => (
              <details key={question} className="py-4">
                <summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="picketops-revenue-surface rounded-2xl border border-primary/25 p-6 sm:p-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Start with your current work</p>
          <h2 className="mt-2 text-3xl font-heading font-semibold">Put one real lead or estimate through PicketOps.</h2>
          <p className="mt-3 mx-auto max-w-2xl text-sm leading-relaxed text-muted-foreground">The fastest way to evaluate contractor software is to use it on a job already sitting on your desk—not a fabricated demo workflow.</p>
          <Button asChild size="lg" className="mt-6"><Link to={trialTarget}>{isAuthenticated ? 'Open your dashboard' : 'Create contractor account'} <ApperIcon name="ArrowRight" size={16}/></Link></Button>
          {!isAuthenticated && <p className="mt-3 text-xs text-muted-foreground">Request operator activation after company setup. The founding offer is 30 days free, then $49/month; support confirms your dates and plan terms. Signup does not start billing.</p>}
        </section>
      </main>
    </div>
  );
}

export const route = { path: '/fence-contractor-software', layout: 'public' };
